/**
 * Call feature bootstrap. Loaded on every authenticated page (in the base
 * bundle), it is deliberately light: it wires the websocket call events to the
 * state machine and exposes a small public surface for the thread header. The
 * heavy media path (livekit-client) is only pulled in lazily by call-state when
 * a call actually starts.
 */

import { callState } from './call-state.js';
import { callApi } from './call-api.js';

function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

function detail(event) {
    return (event && event.detail) || {};
}

function registerSocketBridge() {
    document.addEventListener('ws:call-ring', (e) => {
        const d = detail(e);
        callState.incomingRing(d.call, d.caller, d.livekit_url);
    });

    document.addEventListener('ws:call-accept', (e) => {
        const d = detail(e);
        callState.remoteAccepted(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-decline', (e) => {
        const d = detail(e);
        callState.remoteDeclined(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-cancel', (e) => {
        const d = detail(e);
        callState.remoteCanceled(d.call_id, d.state_version, d.reason);
    });

    document.addEventListener('ws:call-answered-elsewhere', (e) => {
        const d = detail(e);
        callState.answeredElsewhere(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-end', (e) => {
        const d = detail(e);
        callState.remoteEnded(d.call_id, d.state_version, d.duration);
    });

    document.addEventListener('ws:call-auth-accepted', (e) => {
        const d = detail(e);
        const name = d.sender?.name || d.sender?.username || '';
        window.showSuccess?.(t('common.calls_toast_auth_accepted', { username: name }));
    });
    // ws:call-auth-request is surfaced by the persistent notification + push and
    // consumed by the thread buttons for a live refresh, so no toast here.

    // A cancel/decline/end delivered while the socket was down is lost, so on
    // every (re)connect the state machine re-fetches the call it is busy with
    // and closes it locally if the backend already did. No-op when idle.
    document.addEventListener('ws:connected', () => {
        callState.reconcileAfterReconnect();
    });
}

function registerEndedToasts() {
    callState.on('ended', (summary) => {
        const username = summary.other?.username || '';
        const name = summary.other?.name || username;
        const avatar = summary.other?.avatar || '';
        const isVideo = summary.type === 'video';

        switch (summary.toast) {
            case 'missed':
                window.showPushNotification?.({
                    username,
                    url: username ? '/' + username : '',
                    img_url: avatar,
                    title: isVideo ? t('common.calls_toast_missed_video') : t('common.calls_toast_missed_audio'),
                    text: name,
                });
                break;
            case 'ended':
                window.showPushNotification?.({
                    username,
                    url: username ? '/' + username : '',
                    img_url: avatar,
                    title: t('common.calls_toast_ended'),
                    text: name,
                });
                break;
            case 'declined':
                window.showInfo?.(t('common.calls_toast_declined', { username: name }));
                break;
            case 'no_answer':
                window.showInfo?.(t('common.calls_toast_no_answer'));
                break;
            case 'canceled':
                window.showInfo?.(t('common.calls_toast_canceled'));
                break;
            case 'connect_failed':
                window.showError?.(t('common.calls_toast_connect_failed'));
                break;
            case 'unavailable':
                window.showError?.(t('common.calls_toast_unavailable'));
                break;
            default:
                break;
        }
    });
}

registerSocketBridge();
registerEndedToasts();

/**
 * Revoke call authorization for the open conversation's contact (the thread
 * header "turn off calls" control). Reversible: the other side can request
 * again. Refreshes the affordance in place.
 */
async function stopCalls() {
    const uid = window.get_active_thread_uid && window.get_active_thread_uid();
    if (!uid) {
        return;
    }
    try {
        await callApi.revokeAuthorization(uid);
        window.showSuccess?.(t('common.calls_toast_calls_off'));
        document.dispatchEvent(new CustomEvent('calls:authorization-changed', {
            detail: { userId: String(uid) },
        }));
    } catch (e) {
        window.showError?.(t('common.calls_error_unavailable'));
    }
}

// Public surface for the thread header and any other caller.
window.InterpalsCalls = {
    startCall: (targetId, type, other) => callState.startOutgoing(targetId, type, other),
    requestAuthorization: (targetId) => callApi.requestAuthorization(targetId),
    revokeAuthorization: (userId) => callApi.revokeAuthorization(userId),
    stopCalls,
    state: callState,
};
